import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { IconButton } from '@/lib';
import { EDataSourceStatus } from '@/models/dataSource';
import { useTriggerDataSourceExtractionMutation } from '@/services/dataSources';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivationStatus } from '../../../status';
import { OverviewTabProps } from '../types/types';
import { SourceConnectorJobHistoryTable } from './SourceConnectorJobHistoryTable';

export const PendingSourceConnectorDataSource: FC<OverviewTabProps> = ({ dataSource }) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();
  const [triggerExtraction, { isLoading }] = useTriggerDataSourceExtractionMutation();
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [isRunInProgress, setIsRunInProgress] = useState(false);

  const handleRunNow = async () => {
    if (!dataSource?.id) return;
    setExtractionError(null);
    try {
      await triggerExtraction({ workspaceId, dataSourceId: dataSource.id, operation: 'SYNC' }).unwrap();
      setIsRunInProgress(true);
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        setExtractionError(t('detail.source_connector.extraction_in_progress'));
        setIsRunInProgress(true);
      } else {
        setExtractionError(t('detail.source_connector.extraction_failed'));
      }
    }
  };

  const pollInterval = useMemo(() => (isRunInProgress ? 5000 : 0), [isRunInProgress]);

  return (
    <div className='flex flex-col gap-6'>
      <ActivationStatus dataSource={dataSource} />

      <div className='rounded-xl border border-secondary-200 bg-secondary-background p-4'>
        <p className='text-sm text-secondary-700'>{t('detail.source_connector.pending_description')}</p>
        <div className='mt-4'>
          <Loadable isLoading={isLoading}>
            <IconButton
              className='inline-flex'
              text={t('detail.source_connector.run_now')}
              onClick={handleRunNow}
              disabled={isLoading}
            />
          </Loadable>
          {extractionError && <p className='mt-2 text-sm text-red-600'>{extractionError}</p>}
        </div>
      </div>

      <SourceConnectorJobHistoryTable
        connectorFlowInstanceId={dataSource?.connectorFlowInstanceId}
        pollInterval={pollInterval}
      />
    </div>
  );
};

export const ActiveSourceConnectorDataSource: FC<OverviewTabProps> = ({ dataSource }) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();
  const [triggerExtraction, { isLoading }] = useTriggerDataSourceExtractionMutation();
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [isRunInProgress, setIsRunInProgress] = useState(false);

  const handleRunNow = async () => {
    if (!dataSource?.id) return;
    setExtractionError(null);
    try {
      await triggerExtraction({ workspaceId, dataSourceId: dataSource.id, operation: 'SYNC' }).unwrap();
      setIsRunInProgress(true);
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        setExtractionError(t('detail.source_connector.extraction_in_progress'));
        setIsRunInProgress(true);
      } else {
        setExtractionError(t('detail.source_connector.extraction_failed'));
      }
    }
  };

  const pollInterval = useMemo(() => (isRunInProgress ? 5000 : 0), [isRunInProgress]);

  return (
    <div className='flex flex-col gap-6'>
      <ActivationStatus dataSource={dataSource} />

      <div className='flex items-center gap-4'>
        <Loadable isLoading={isLoading}>
          <IconButton
            className='inline-flex'
            text={t('detail.source_connector.run_now')}
            onClick={handleRunNow}
            disabled={isLoading}
          />
        </Loadable>
        {dataSource?.status === EDataSourceStatus.ACTIVE && (
          <span className='text-sm text-green-700'>{t('detail.source_connector.active_label')}</span>
        )}
        {extractionError && <p className='text-sm text-red-600'>{extractionError}</p>}
      </div>

      <SourceConnectorJobHistoryTable
        connectorFlowInstanceId={dataSource?.connectorFlowInstanceId}
        pollInterval={pollInterval}
      />
    </div>
  );
};
